import React, { useState, useEffect, useRef } from 'react';
import axiosInstance from '../utils/axiosInstance';
import config from '../utils/config';
import { Select, SelectContent, SelectItem, SelectSeparator, SelectTrigger, SelectValue } from './pilot-ui/select';
import {
  addDaysToDateInput,
  formatApiDateForInput,
  formatDateInputForDisplay,
  isValidDateRange,
  normalizeDateRange,
} from '../utils/dateRange';

interface Device {
  id: number;
  name: string;
  created_date: string;
  calibration_offset: number;
  centroids: null | any;
}

/** One recorded filter epoch, intersected with the analysis's own data. */
interface FilterPeriod {
  epoch_id: string;
  effective_date: string | null;
  analysis_start: string;
  analysis_end: string;
  epoch_start: string;
  epoch_end: string | null;
  is_current: boolean;
  source: string;
  sources: string[];
  filter: Record<string, unknown> | null;
  filter_label: string | null;
  processed_day_count: number;
  availability: 'available' | 'empty';
}

interface FilterPeriodsResponse {
  analysis_key: string;
  periods: FilterPeriod[];
}

// Fixed quick-range menu items shown above the filter change periods.
// Ranges are anchored to the end of the newest period rather than today, so
// they stay close to the processed-data window; the parent clamps and snaps
// whatever is selected here into the exact window.
const QUICK_RANGES = [
  { value: 'last30', label: 'Last 30 days', days: 30 },
  { value: 'last90', label: 'Last 90 days', days: 90 },
];

interface FilterDateSelectorProps {
  /** Tells comparison navigation when the asynchronous period selection is ready. */
  onReadyChange?: (ready: boolean) => void;
  selectedDevice: string;
  /** FS-508: the third argument is the selected period's `epoch_id`, or null
   *  for a quick range. The analysis request needs it to describe the filter
   *  that was installed during the period rather than the one in the device
   *  today. It rides along with the dates deliberately: they are one
   *  selection, and two callbacks could disagree about which period is
   *  showing. The fourth is false for a period with no processed days: its
   *  dates lie outside the processed window, and the parent must run on them
   *  as they are rather than widen them to the whole window (which charted
   *  the OLD filters under the new filter's name). */
  onDateRangeSelect: (
    startDate: string,
    endDate: string,
    epochId: string | null,
    hasProcessedDays?: boolean,
  ) => void;
  disabled: boolean;
  devices: Device[];
  externalStartDate?: string;
  externalEndDate?: string;
  /** When true, the caller's date range is locked (Pin Analysis). Skip the
   *  auto-fetch and auto-select so the locked dates aren't overwritten. */
  datesLocked?: boolean;
  /** FS-508: periods are per-analysis — each analysis has its own
   *  daily_calcs history, so the same epoch can be selectable under one and
   *  empty under another. Changing it refetches. */
  analysisKey?: string;
  /** FS-508: whether the page's current range still came from this
   *  dropdown. The parent clamps and snaps every selection into the
   *  processed window, so the range it echoes back rarely matches the
   *  period exactly — comparing dates would clear the selection on almost
   *  every pick. Only a manual date edit (this goes false) clears it. */
  isFilterPeriod?: boolean;
  selectedEpochId?: string | null;
}

/**
 * FS-508: label a period the way an operator reads it —
 *   "Current — MERV 7 — 08/10/2026 to present"
 *   "Previous — MERV 11 — 05/22/2026 to 08/08/2026"
 * The install date leads, not the first analyzable day: the operator is
 * picking the filter that went in on that date. Identity is dropped when we
 * don't know it, leaving plain dates rather than a guess.
 */
export function periodLabel(period: FilterPeriod, index: number): string {
  const parts: string[] = [];
  if (period.effective_date === null) {
    parts.push(period.is_current ? 'All data' : 'Before first recorded change');
  } else if (period.is_current) {
    parts.push('Current');
  } else if (index === 1) {
    parts.push('Previous');
  } else {
    parts.push('Earlier');
  }

  if (period.filter_label) parts.push(period.filter_label);

  const start = formatDateInputForDisplay(period.effective_date ?? period.analysis_start);
  const end = period.is_current ? 'present' : formatDateInputForDisplay(period.analysis_end);
  parts.push(`${start} to ${end}`);

  if (period.availability === 'empty') parts.push('no processed days yet');

  return parts.join(' — ');
}

const FilterDateSelector: React.FC<FilterDateSelectorProps> = ({
  selectedDevice,
  onDateRangeSelect,
  disabled,
  devices,
  externalStartDate,
  externalEndDate,
  datesLocked = false,
  analysisKey,
  isFilterPeriod = true,
  selectedEpochId,
  onReadyChange,
}) => {
  const [periods, setPeriods] = useState<FilterPeriod[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  // The epoch_id (or quick-range key) currently selected. Epoch ids are
  // stable across refetches, so an analysis switch keeps the same filter
  // period selected instead of silently jumping to another one.
  const [selectedValue, setSelectedValue] = useState<string>('');

  const validPeriods = (list: FilterPeriod[]): FilterPeriod[] =>
    list.filter((p) => isValidDateRange(p.analysis_start, p.analysis_end));

  // Fetch responses can land out of order across device/analysis switches —
  // only the newest request may write state, or the old device's periods
  // land as the new device's.
  const requestIdRef = useRef(0);
  const wasLockedRef = useRef(false);

  useEffect(() => {
    let cancelled = false;

    const fetchPeriods = async () => {
      onReadyChange?.(false);
      // When dates are locked (Pin Analysis), skip the fetch entirely —
      // auto-selecting a period would overwrite the locked range. Clear any
      // stale periods from a previous device so the dropdown doesn't lie.
      if (datesLocked || !selectedDevice) {
        if (datesLocked) wasLockedRef.current = true;
        setPeriods([]);
        setSelectedValue('');
        onReadyChange?.(true);
        return;
      }

      const device = devices?.find((d) => d.name === selectedDevice);
      if (!device) {
        setPeriods([]);
        setSelectedValue('');
        setError('Failed to fetch analysis periods');
        onReadyChange?.(true);
        return;
      }

      const requestId = ++requestIdRef.current;
      setLoading(true);
      setError('');

      try {
        const response = await axiosInstance.get<FilterPeriodsResponse>(
          `${config.notesApiUrl}/notes/filter_periods/${device.id}`,
          { params: analysisKey ? { analysis_key: analysisKey } : {} },
        );
        if (cancelled || requestId !== requestIdRef.current) return;

        const fetched = validPeriods(response.data?.periods ?? []);
        setPeriods(fetched);

        // Unlocking a comparison must not replace its dates with the newest
        // period. Restore the route Clip's period without emitting a new range.
        if (wasLockedRef.current) {
          wasLockedRef.current = false;
          setSelectedValue(isFilterPeriod ? selectedEpochId ?? '' : '');
          return;
        }

        // Default to the newest period that HAS data. That is normally the
        // current epoch; right after a filter change it is the previous
        // one, because the nightly runs two days behind and the new epoch
        // has nothing yet. Defaulting to an empty period would blank the
        // whole screen for two days after every change — the empty current
        // epoch is still listed, and labelled, so it is never hidden.
        const preferred = fetched.find((p) => p.availability === 'available') ?? fetched[0];
        if (preferred) {
          setSelectedValue(preferred.epoch_id);
          onDateRangeSelect(
            preferred.analysis_start,
            preferred.analysis_end,
            preferred.epoch_id,
            preferred.availability !== 'empty',
          );
        } else {
          setSelectedValue('');
        }
      } catch (err) {
        if (cancelled || requestId !== requestIdRef.current) return;
        console.error('Error fetching filter periods:', err);
        setPeriods([]);
        setSelectedValue('');
        setError('Failed to fetch analysis periods');
      } finally {
        if (!cancelled && requestId === requestIdRef.current) {
          setLoading(false);
          onReadyChange?.(true);
        }
      }
    };

    fetchPeriods();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDevice, devices, datesLocked, analysisKey]);

  // Resolve a dropdown value (quick-range key or epoch id) to its date
  // pair. Quick ranges anchor to the end of the newest period. NOTE: the
  // period API bounds each epoch by the requested analysis's own processed
  // days, but the parent still clamps and snaps every selection into the
  // exact window it holds.
  const rangeForSelection = (value: string): [string, string] | null => {
    const quick = QUICK_RANGES.find((q) => q.value === value);
    if (quick) {
      const anchorEnd = periods[0]?.analysis_end;
      if (!anchorEnd) return null;
      return [addDaysToDateInput(anchorEnd, -(quick.days - 1)), anchorEnd];
    }

    const period = periods.find((p) => p.epoch_id === value);
    return period ? [period.analysis_start, period.analysis_end] : null;
  };

  // A manual date edit on the page means the range is no longer this
  // dropdown's — drop the selection. A clamped or snapped range still IS
  // the selection (FS-508: clipping must not clear it), so the dates
  // themselves are deliberately not compared.
  useEffect(() => {
    if (!isFilterPeriod && selectedValue !== '') {
      setSelectedValue('');
    }
  }, [isFilterPeriod, externalStartDate, externalEndDate, selectedValue]);

  const handleFilterPeriodChange = (newValue: string) => {
    setSelectedValue(newValue);

    const range = rangeForSelection(newValue);
    if (range) {
      const normalized = normalizeDateRange(range[0], range[1]);
      // A quick range is a window, not a period: it can span several filters,
      // so there is no epoch to describe and the response falls back to the
      // device's current filter exactly as it always has.
      const isQuickRange = QUICK_RANGES.some((q) => q.value === newValue);
      const period = periods.find((p) => p.epoch_id === newValue);
      onDateRangeSelect(
        formatApiDateForInput(normalized.startDate),
        formatApiDateForInput(normalized.endDate),
        isQuickRange ? null : newValue,
        period ? period.availability !== 'empty' : true,
      );
    }
  };

  if (error) {
    return (
      <div role="alert" className="text-red-700 text-sm mt-2">
        {error}
      </div>
    );
  }

  // The current epoch is always listed, but when it has no processed days
  // the default selection sits on an older one — say so, or the operator
  // reads the dropdown as "my filter change never registered".
  const currentEpoch = periods.find((p) => p.is_current);
  const currentEmpty =
    currentEpoch &&
    currentEpoch.availability === 'empty' &&
    currentEpoch.effective_date !== null;
  const calibrating = currentEmpty && selectedValue !== currentEpoch.epoch_id;
  // Picked anyway: the analysis runs on the new filter's own dates, so the
  // charts come back empty. Say why, and where the readings so far are.
  const currentSelectedEmpty = currentEmpty && selectedValue === currentEpoch.epoch_id;

  return (
    <div className="space-y-2">
      <label htmlFor="analysis-period" className="block text-sm font-medium">
        Analysis Periods
      </label>
      <Select
        value={selectedValue}
        items={[
          { value: '', label: 'Select an analysis period' },
          ...QUICK_RANGES.map((range) => ({ value: range.value, label: range.label })),
          ...periods.map((period, index) => ({ value: period.epoch_id, label: periodLabel(period, index) })),
        ]}
        onValueChange={(value) => handleFilterPeriodChange(value ?? '')}
        disabled={disabled || loading || !selectedDevice || periods.length === 0}
      >
        <SelectTrigger id="analysis-period" className="w-full" aria-describedby="analysis-period-help" title="Choose a quick range or a recorded filter period">
          <SelectValue placeholder="Select an analysis period" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="">Select an analysis period</SelectItem>
          {QUICK_RANGES.map((q) => (
            <SelectItem key={q.value} value={q.value}>{q.label}</SelectItem>
          ))}
          <SelectSeparator />
          {periods.map((period, index) => (
            <SelectItem key={period.epoch_id} value={period.epoch_id}>
              {periodLabel(period, index)}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      <p id="analysis-period-help" className="text-xs text-muted-foreground">Sets the dates to a recent range or one filter installation period.</p>
      {calibrating && (
        <div className="text-muted-foreground text-sm">
          {`Current filter (installed ${formatDateInputForDisplay(currentEpoch!.effective_date!)}) `}
          has no processed days yet.
        </div>
      )}
      {currentSelectedEmpty && (
        <div className="text-status-warning-foreground text-sm">
          {`No processed days for this filter yet (installed ${formatDateInputForDisplay(currentEpoch!.effective_date!)}), `}
          so the charts will be empty. The nightly usually processes the first
          day within a couple of days of the change. To see the readings so
          far, use View Raw Scatter Chart below the results.
        </div>
      )}
      {loading && (
        <div className="text-muted-foreground text-sm">
          Loading analysis periods...
        </div>
      )}
    </div>
  );
};

export default FilterDateSelector;
